/**
 * Returns a function, that, as long as it continues to be invoked,
 * will not be triggered before the wait milliseconds.
 *
 * @param {Function} func function to be called
 * @param {Number} wait time in milliseconds to run given func
 * @param {Boolean | { immediate?: boolean, withId?:boolean }} options boolean for immediate or object with options
 * @param {Boolean} options.immediate trigger the func on the leading edge instead of the trailing
 * @param {Boolean} options.withId enable IDs mode (requires debounceId as first argument)
 *
 * @returns {Function} debounced function
 */
export default function debounce(func, wait = 200, options = {}) {
  if (typeof options === 'boolean') {
    options = { immediate: options }
  }
  const { immediate = false, withId = false } = options
  if (withId) {
    const timeouts = new Map()
    return function (debounceId, ...args) {
      const context = this
      if (debounceId === undefined) {
        throw new Error('debounceId is required as first argument')
      }
      const existingTimeout = timeouts.get(debounceId)
      const later = function () {
        timeouts.delete(debounceId)
        if (!immediate) func.apply(context, args)
      }
      const callNow = immediate && !existingTimeout
      if (existingTimeout) {
        clearTimeout(existingTimeout)
      }
      const newTimeout = setTimeout(later, wait)
      timeouts.set(debounceId, newTimeout)
      if (callNow) func.apply(context, args)
    }
  }
  let timeout
  return function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) func.apply(context, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(context, args)
  }
}
